import React, { useState } from 'react';
import { Box, Flex, Text, Input, Textarea, HStack } from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { IconButton } from 'src/components/atoms/IconButton';
import { SendMessagePayload } from 'src/api/inboxes';

type NewMessageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSend?: (data: SendMessagePayload) => void;
  isLoading?: boolean;
};

export const NewMessageModal: React.FC<NewMessageModalProps> = ({
  isOpen,
  onClose,
  onSend,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [recipientUserIds, setRecipientUserIds] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const handleClose = () => {
    setRecipientUserIds([]);
    setSubject('');
    setBody('');
    setIsExpanded(false);
    onClose();
  };

  const handleSend = () => {
    if (onSend) {
      onSend({
        recipientUserIds,
        title: subject,
        description: body,
      });
    }
    handleClose();
  };

  const handleDelete = () => {
    setRecipientUserIds([]);
    setSubject('');
    setBody('');
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleRecipientsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRecipientUserIds(e.target.value.split(',').map((id) => id.trim()));
  };

  if (!isOpen) return null;

  const inputStyles = {
    border: '1px solid',
    borderColor: 'border.subtle',
    borderRadius: 'md',
    fontFamily: 'marketingPage',
    fontSize: '14px',
    _focus: {
      borderColor: 'icon.green',
      boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
    },
    _placeholder: {
      color: 'gray.400',
    },
  };

  const compactModal = (
    <Box
      position="fixed"
      bottom={6}
      right={6}
      width="400px"
      bg="surface.card"
      borderRadius="16px"
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
      zIndex={1000}
      overflow="hidden">
      {/* Header */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        px={4}
        py={3}
        borderBottom="1px solid"
        borderColor="border.subtle">
        <Text
          fontFamily="marketingPage"
          fontWeight="bold"
          fontSize="lg"
          color="text.title">
          New Message
        </Text>
        <HStack spacing={1}>
          <IconButton
            aria-label="Expand"
            icon={<Icon iconName="expand" boxSize={4} />}
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
          />
          <IconButton
            aria-label="Close"
            icon={<Icon iconName="close" boxSize={3} />}
            variant="ghost"
            size="sm"
            onClick={handleClose}
          />
        </HStack>
      </Flex>

      {/* Form */}
      <Box px={4} py={3}>
        <Box mb={3}>
          <Text
            fontFamily="marketingPage"
            fontSize="12px"
            color="gray.500"
            mb={1}>
            To
          </Text>
          <Input
            type="email"
            placeholder="example@mail.com"
            value={recipientUserIds.join(', ')}
            onChange={handleRecipientsChange}
            {...inputStyles}
          />
        </Box>

        <Box mb={3}>
          <Text
            fontFamily="marketingPage"
            fontSize="12px"
            color="gray.500"
            mb={1}>
            Subject
          </Text>
          <Input
            placeholder="Type your subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            {...inputStyles}
          />
        </Box>

        <Textarea
          placeholder="Type your message..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          minH="150px"
          resize="none"
          {...inputStyles}
        />
      </Box>

      {/* Footer */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        px={4}
        py={3}
        borderTop="1px solid"
        borderColor="border.subtle">
        <HStack spacing={2}>
          <Button
            buttonName="send-message"
            bg="icon.green"
            color="white"
            size="md"
            px={8}
            borderRadius="md"
            fontWeight="semibold"
            fontFamily="marketingPage"
            onClick={handleSend}
            isLoading={isLoading}
            _hover={{ bg: 'green.500' }}>
            Send
          </Button>
        </HStack>
        <IconButton
          aria-label="Delete"
          icon={<Icon iconName="trash" boxSize={4} color="gray.500" />}
          variant="ghost"
          size="sm"
          onClick={handleDelete}
        />
      </Flex>
    </Box>
  );

  const expandedModal = (
    <>
      {/* Overlay */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.400"
        zIndex={999}
        onClick={handleClose}
      />

      {/* Modal */}
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        width="900px"
        maxW="90vw"
        bg="surface.card"
        borderRadius="16px"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
        zIndex={1000}
        overflow="hidden">
        {/* Header */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          px={6}
          py={4}
          borderBottom="1px solid"
          borderColor="border.subtle">
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="xl"
            color="text.title">
            New Message
          </Text>
          <HStack spacing={1}>
            <IconButton
              aria-label="Minimize"
              icon={<Icon iconName="minimize" boxSize={4} />}
              variant="ghost"
              size="sm"
              onClick={toggleExpanded}
            />
            <IconButton
              aria-label="Close"
              icon={<Icon iconName="close" boxSize={3} />}
              variant="ghost"
              size="sm"
              onClick={handleClose}
            />
          </HStack>
        </Flex>

        {/* Content */}
        <Flex>
          {/* Form Section */}
          <Box flex={1} px={6} py={4}>
            <Box mb={4}>
              <Text
                fontFamily="marketingPage"
                fontSize="12px"
                color="gray.500"
                mb={1}>
                To
              </Text>
              <Input
                placeholder="example@mail.com"
                value={recipientUserIds.join(', ')}
                onChange={handleRecipientsChange}
                {...inputStyles}
              />
            </Box>

            <Box mb={4}>
              <Text
                fontFamily="marketingPage"
                fontSize="12px"
                color="gray.500"
                mb={1}>
                Subject
              </Text>
              <Input
                placeholder="Type your subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                {...inputStyles}
              />
            </Box>

            <Textarea
              placeholder="Type your message..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              minH="250px"
              resize="none"
              {...inputStyles}
            />
          </Box>
        </Flex>

        {/* Footer */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
          px={6}
          py={4}
          borderTop="1px solid"
          borderColor="border.subtle">
          <HStack spacing={3}>
            <Button
              buttonName="send-message-expanded"
              bg="icon.green"
              color="white"
              size="md"
              px={8}
              borderRadius="md"
              fontWeight="semibold"
              fontFamily="marketingPage"
              onClick={handleSend}
              isLoading={isLoading}
              _hover={{ bg: 'green.500' }}>
              Send
            </Button>
          </HStack>
          <IconButton
            aria-label="Delete"
            icon={<Icon iconName="trash" boxSize={4} color="gray.500" />}
            variant="ghost"
            size="sm"
            onClick={handleDelete}
          />
        </Flex>
      </Box>
    </>
  );

  return isExpanded ? expandedModal : compactModal;
};
